<template>
    <div class="accordion-container">
        <div v-for="(item, index) in items" :key="index" class="accordion-item">
            <button :class="['accordion', { active: item.isOpen }]" @click="toggleAccordion(index)">
                <div class="accordion-header">
                    <div class="accordion-arrow">
                        <MdIconArrowCaret :rotate="item.isOpen ? 'down' : 'right'" color="spirits--600" />
                    </div>
                    <div class="accordion-content">
                        <slot name="header" :item="item" :index="index">
                            <div class="default-header">
                                <div class="title">{{ item.title || item.name }}</div>
                                <div v-if="item.subtitle" class="subtitle">{{ item.subtitle }}</div>
                            </div>
                        </slot>
                    </div>
                </div>
            </button>

            <div v-if="item.isOpen" :class="['panel']"
                :style="{ maxHeight: item.isOpen ? 'none' : '0px' }">
                <div class="panel-content">
                    <slot name="content" :item="item" :index="index">
                        <div class="default-content">
                            {{ item.content }}
                        </div>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { MdIconArrowCaret } from '@antiqua-dls/vue-components'

export default {
    name: 'Accordion',
    components: {
        MdIconArrowCaret
    },
    props: {
        items: {
            type: Array,
            required: true,
            default: () => []
        },
        maxOpen: {
            type: Number,
            default: 1,
            validator: (value) => value > 0
        }
    },

    emits: ['toggle'],

    data() {
        return {
            openOrder: []
        }
    },

    methods: {
        toggleAccordion(index) {
            if (this.maxOpen) {
                const currentItem = this.items[index]
                const isCurrentlyOpen = currentItem.isOpen

                if (!isCurrentlyOpen) {
                    const openCount = this.items.filter(item => item.isOpen).length
                    if (openCount >= this.maxOpen) {
                        // Close the oldest opened accordion using FIFO order
                        if (this.openOrder.length > 0) {
                            const oldestIndex = this.openOrder.shift()
                            if (typeof oldestIndex === 'number' && this.items[oldestIndex]) {
                                this.items[oldestIndex].isOpen = false
                            }
                        } else {
                            // Fallback: close the first open one if order is missing
                            const firstOpenIdx = this.items.findIndex(item => item.isOpen)
                            if (firstOpenIdx !== -1) {
                                this.items[firstOpenIdx].isOpen = false
                            }
                        }
                    }
                    this.openOrder.push(index)
                } else {
                    // Remove from order when closing
                    const pos = this.openOrder.indexOf(index)
                    if (pos !== -1) this.openOrder.splice(pos, 1)
                }
                currentItem.isOpen = !isCurrentlyOpen
            }
            this.$emit('toggle', { index, item: this.items[index] })
        }
    }
}
</script>

<style lang="scss" scoped>
.accordion-container {

    .accordion {
        color: var(--md-color--spirits--900);
        cursor: pointer;
        padding: 12px 40px;
        width: 100%;
        text-align: left;
        border: none;
        outline: none;
        transition: all 0.3s ease;
        border-bottom: 1px solid var(--md-color--spirits--300);
        margin-bottom: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &:hover {
            background-color: var(--md-color--spirits--50);
        }

        &.active {
            background-color: var(--md-color--spirits--50);
        }
    }

    .accordion-header {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        width: 100%;

        .accordion-content {
            flex: 1;
        }

        .accordion-arrow {
            transition: transform 0.3s ease;
            padding-top: 0.25rem;

            &.rotated {
                transform: rotate(180deg);
            }
        }
    }

    .default-header {
        .title {
            font-weight: 600;
            color: var(--md-color--spirits--900);
            margin-bottom: var(--md-spacing-unit--2xs);
        }

        .subtitle {
            font-size: 0.875rem;
            color: var(--md-color--spirits--600);
        }
    }

    .panel {
        background-color: white;
        overflow: hidden;
        transition: all 0.3s ease-out;

        .panel-content {
            padding: 1rem 2.5rem;
            border-bottom: 1px solid var(--md-color--spirits--300);
        }
    }

    .default-content {
        color: var(--md-color--spirits--700);
        line-height: 1.5;
    }
}
</style>
